import { InputV2 } from '@cimplico/components';
import { FieldType, WorksheetSchemaField } from '../../../../../types/custom-worksheet-v2';
import { useState } from 'react';
import {
    formatValueBasedOnTypes,
    getZIndex,
    isMonetaryType,
    isNonConvertibleText,
    isNonConvertibleType,
    isUnformattedType,
} from '../../utils';
import { addAdditionalField, editField } from '../../store/slice';
import { useDispatch } from 'react-redux';
import { PROPERTY_NAME_CHAR_LIMIT } from '../../../../../constants';
import { isKeyword } from '../../../../../components/custom-worksheet/utils';
import CWFieldInput from '../../../../../components/custom-worksheet/CWFieldInput';
import EditInput from '../../../../custom-worksheet-template/form/components/EditInput';
import FlashBackground from '../FlashBackground';
import CWInputControlWrapper from '../../../../../components/custom-worksheet/CWInputControlWrapper';

type NewOrEditAdditionalFieldProps = {
    allowedTypes: FieldType[];
    sectionId?: string;
    field?: WorksheetSchemaField;
    onClose: (resetEditingField?: boolean) => void;
};

function NewOrEditAdditionalField({ field, sectionId, allowedTypes, onClose }: NewOrEditAdditionalFieldProps): JSX.Element {
    const [value, setValue] = useState(field ? field.value : null);
    const [nameError, setNameError] = useState<string | undefined>(undefined);
    const [updatedField, setUpdatedField] = useState({
        name: field ? field.name : '',
        value: field ? field.value : null,
        type: field ? field.type : allowedTypes[0],
    });

    const dispatch = useDispatch();
    const showFieldTypeSelect = allowedTypes.length > 1;

    function handleFieldTypeChange(val?: string): void {
        const newType = val ? (val as FieldType) : FieldType.MONEY;
        // Reset value on type change
        if (!field || isNonConvertibleType(updatedField.type, newType) || isNonConvertibleText(field.type, field.value)) {
            setValue(null);
            setUpdatedField((prev) => ({ ...prev, value: null, type: newType }));
            return;
        }

        // If previous and new type is a TEXT/NUMBER or MONEY/ACCOUNTING, don't format the value because they are the same format already
        if (isMonetaryType(field.type, newType) || isUnformattedType(field.type, newType)) {
            setUpdatedField((prev) => ({ ...prev, type: newType }));
            return;
        }

        // Else, format the current value to revert conversion
        const newValue = formatValueBasedOnTypes(field.value, field.type, newType);
        setValue(newValue);
        setUpdatedField((prev) => ({ ...prev, value: newValue, type: newType }));
    }

    function handleSave(resetEditingField?: boolean, ignoreError?: boolean): void {
        let _value = updatedField.value;

        if (_value === '' || _value === null) {
            _value = null;
        }

        const numberTypes = [FieldType.NUMBER, FieldType.PERCENT, FieldType.ACCOUNTING, FieldType.MONEY];

        if (numberTypes.includes(updatedField.type) && (updatedField.value === '.' || updatedField.value === '-')) {
            return;
        }

        // If value entered is greater than the MAX_SAFE_INTEGER, don't save the value
        if (typeof updatedField.value === 'number' && updatedField.value > Number.MAX_SAFE_INTEGER) {
            return;
        }

        const updatedName = updatedField.name.trim();

        // Prevent saving if field name is incomplete
        if (!updatedName) {
            if (ignoreError) {
                onClose(resetEditingField);
                return;
            }

            setNameError('A field name must be provided.');
            return;
        }

        if (isKeyword(updatedName)) {
            setNameError(`Invalid name: ${updatedName} is a keyword`);
            return;
        }

        // If no field is provided, it means that we are creating new additional field
        if (!field && sectionId) {
            dispatch(
                addAdditionalField({
                    sectionId,
                    name: updatedName,
                    type: updatedField.type,
                    value: _value,
                })
            );
        }

        // If field is provided, it means that we are updating the additional field
        if (field) {
            dispatch(
                editField({
                    id: field.id,
                    details: {
                        type: field.type !== updatedField.type ? updatedField.type : undefined,
                        value: field.value !== _value ? _value : undefined,
                        name: updatedName !== field.name ? updatedName : undefined,
                    },
                })
            );
        }

        onClose(resetEditingField);
    }

    return (
        <CWInputControlWrapper
            zIndex={getZIndex(field?.id || `${sectionId}.properties.new` || '')}
            onBlur={(resetEditingField): void => handleSave(resetEditingField, true)}
            onSave={handleSave}
            onCancel={onClose}
        >
            <div
                data-field="true"
                id={field?.id || 'new'}
                className={`scroll-mt-8 grid ${
                    showFieldTypeSelect ? 'lg:grid-cols-[2fr_2fr] lg:mr-0 lg:gap-4' : 'md:grid-cols-[2fr_2fr] md:mr-0 md:gap-4'
                } mr-0.5 gap-2 relative`}
            >
                {field && <FlashBackground id={field.id} />}
                <div>
                    {nameError && <p className="text-xs text-red-600 absolute -top-5">{nameError}</p>}
                    <InputV2
                        autoFocus
                        placeholder="Enter a name..."
                        value={updatedField.name}
                        isError={nameError !== undefined}
                        characterLimit={PROPERTY_NAME_CHAR_LIMIT}
                        showCharacterLimitHint={false}
                        type="text"
                        onChange={(value): void => {
                            setUpdatedField((prev) => ({ ...prev, name: value }));
                            setNameError(undefined);
                        }}
                    />
                </div>
                <div className={`${showFieldTypeSelect ? 'grid grid-cols-[1fr_1fr]' : ''} gap-4`}>
                    {showFieldTypeSelect && (
                        <EditInput
                            allowedTypes={allowedTypes}
                            inputType="type"
                            placeholder="Select a field type..."
                            value={updatedField.type}
                            showActionButtons={false}
                            autoFocus={false}
                            onChange={handleFieldTypeChange}
                        />
                    )}
                    <div className={`${showFieldTypeSelect ? 'lg:mr-0.5' : 'md:mr-0.5'}`}>
                        <CWFieldInput
                            autoFocus={false}
                            fieldType={updatedField.type}
                            onValueChange={(value): void => setUpdatedField((prev) => ({ ...prev, value }))}
                            defaultValue={value}
                            showIcon
                        />
                    </div>
                </div>
            </div>
        </CWInputControlWrapper>
    );
}

export default NewOrEditAdditionalField;
